import React, { useState, useEffect, ChangeEvent, ReactNode } from "react";
import { Tooltip } from "react-tooltip";

import CustomCheckbox from "../CustomCheckbox/CustomCheckbox";
import TooltipContentWrapper from "../TooltipContentWrapper/TooltipContentWrapper";
import { saveToLocalStorage } from "src/utils/localStorage";

import "./DismissibleTooltip.scss";

interface DismissibleTooltipProps {
  storageKey: string;
  title: ReactNode;
  description?: ReactNode;
  tooltipId?: string;
  checkboxLabel: ReactNode;
  confirmLabel: ReactNode;
  noArrow?: boolean;
}

const isHidden = (key: string) => localStorage.getItem(key) === "true";

export default function DismissibleTooltip({
  storageKey,
  title,
  description,
  tooltipId = "dismissible-tooltip",
  checkboxLabel,
  confirmLabel,
  noArrow,
}: DismissibleTooltipProps) {
  const [dontShow, setDontShow] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const hidden = isHidden(storageKey);

  const handleCheckbox = (e: ChangeEvent<HTMLInputElement>) => {
    setDontShow(e.target.checked);
  };

  const handleConfirm = () => {
    if (dontShow) saveToLocalStorage(storageKey, true);
    setIsOpen(false);
  };

  useEffect(() => {
    if (hidden) return;
    const timeoutId = setTimeout(() => setIsOpen(true), 300);
    return () => clearTimeout(timeoutId);
  }, [hidden]);

  return (
    <Tooltip
      id={tooltipId}
      clickable
      isOpen={isOpen}
      noArrow={noArrow}
      className="dismissible-tooltip"
    >
      <TooltipContentWrapper title={title} description={description}>
        <div className="dismissible-tooltip__controls">
          <CustomCheckbox
            id={`${tooltipId}-checkbox`}
            checked={dontShow}
            onChange={handleCheckbox}
            label={checkboxLabel}
            data-qa={`dismissible-tooltip-${tooltipId}-checkbox`}
          />

          <button
            type="button"
            className="button-style-reset"
            onClick={handleConfirm}
            data-qa={`dismissible-tooltip-${tooltipId}-confirm-button`}
          >
            {confirmLabel}
          </button>
        </div>
      </TooltipContentWrapper>
    </Tooltip>
  );
}
